import { type FC, type ReactNode } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { emailMustBeValid, emailMustHaveAtSign } from "../../helpers/password-errors";

import { EulaError, serverLogin } from "api/api-server-actions";
import { LOGO_FORM_SIZE } from "components/constants";
import { t } from "i18n/i18n";
import { FormField } from "mds/components/FormField";
import { TextField } from "mds/components/TextField";
import { TrackedLink } from "mds/components/TrackedLink";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ForumLogoIcon, LoadingDotsIcon } from "mds/icons";
import { useNavigate } from "utils/routing";

interface EmailLoginFormValue {
  email: string;
  password: string;
}

interface EmailLoginFormProps {
  onLoggedIn: () => void;
  children?: ReactNode | ReactNode[];
}

export const EmailLoginForm: FC<EmailLoginFormProps> = ({ onLoggedIn, children }) => {
  const passwordResetMessage =
    new URLSearchParams(window.location.search).get("password_reset") === "true";
  const form = useForm<EmailLoginFormValue>();
  const navigate = useNavigate();

  const handleSubmit: SubmitHandler<EmailLoginFormValue> = (formData) =>
    serverLogin(formData.email, formData.password)
      .then(() => {
        onLoggedIn();
      })
      .catch((error) => {
        if (
          error instanceof Error &&
          error.name === "EULA_NOT_ACCEPTED" &&
          (error as EulaError).token &&
          (error as EulaError).orgId
        ) {
          // Redirect to onboarding with token and org_id
          const eulaError = error as EulaError;
          navigate(`/onboarding-invite?token=${eulaError.token}&org_id=${eulaError.orgId}`);
        } else if (error instanceof Error && error.name === "EULA_NOT_ACCEPTED") {
          form.setError("email", {
            type: "server",
            message: t("error.org_not_found"),
          });
        } else {
          form.setError("password", {
            type: "server",
            message: t("error.incorrect_email_or_password"),
          });
        }
      });

  return (
    <div className="m-2 w-full max-w-[18rem] p-4 md:w-96">
      <div className="mb-3 flex items-center justify-between border-b border-black-tint-90 bg-white pb-1 text-black-tint-20">
        <span className="mb-2 flex cursor-pointer items-center">
          <ForumLogoIcon width={LOGO_FORM_SIZE} />
        </span>
      </div>

      <h2 className="mb-3 text-black-tint-20">{t("user_info.login_title")}</h2>

      {passwordResetMessage && (
        <span className="mb-2 flex cursor-pointer">
          <p>{t("user_info.login_after_password_reset")}</p>
        </span>
      )}

      <FormProvider {...form}>
        <form
          className="mb-3 flex w-full flex-col gap-3"
          noValidate
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FormField id="email" label={t("user_info.email")} name="email" required>
            {(controlProps) => (
              <TextField
                {...form.register("email", {
                  required: t("error.field_required"),
                  validate: {
                    emailMustHaveAtSign,
                    emailMustBeValid,
                  },
                })}
                {...controlProps}
                autoComplete="email"
                eventName="Account - Sign in / Email"
                eventPropertyKey="userEmail"
                type="email"
                required
              />
            )}
          </FormField>

          <FormField id="password" label={t("user_info.password")} name="password" required>
            {(controlProps) => (
              <TextField
                {...form.register("password", {
                  required: t("error.field_required"),
                })}
                {...controlProps}
                autoComplete="current-password"
                eventName="Account - Sign in / Password"
                type="password"
                required
              />
            )}
          </FormField>

          <div className="mt-3 flex items-center gap-2">
            <SquareButton
              eventName="Account - Sign in / Confirm"
              kind="accent"
              size="m"
              type="submit"
              value="Login"
            >
              {t("user_info.sign_in")}
            </SquareButton>
            {form.formState.isSubmitting && <LoadingDotsIcon aria-label={t("common.submitting")} />}
          </div>

          {children}
        </form>
      </FormProvider>

      <TrackedLink
        className="mb-2 mt-8 block cursor-pointer text-sm font-normal text-black-tint-40 underline"
        eventName="Account - Sign in / Forgot password"
        id="forgot-password"
        to="/forgot-password"
      >
        {t("user_info.forgot_password")}
      </TrackedLink>
    </div>
  );
};
